import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { z } from 'zod';
import { CasePage } from '../../../features/cases/case-page.tsx';

/**
 * Case page (17e, 20a): driven by the process definition metadata and the case API only. `activity` opens an agent
 * activity's episode (`<activityId>#<iteration>`); `tab` and `item` are the episode's layer and open drawer.
 */
const Search = z.object({
  activity: z.string().optional().catch(undefined),
  tab: z.string().optional().catch(undefined),
  item: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/cases/$caseId')({
  validateSearch: Search,
  component: CaseRoute,
});

function CaseRoute() {
  const { caseId } = Route.useParams();
  const { activity, tab, item } = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  // Review 13: history entries pushed by opening item drawers; closing goes back over them, so Back does not reopen
  // the item (as on `/approvals/$approvalId`).
  const itemDepth = useRef(0);
  useEffect(() => {
    if (!item) itemDepth.current = 0;
  }, [item]);
  const onSearch = useCallback(
    (patch: { activity?: string | undefined; tab?: string | undefined; item?: string | undefined }) => {
      const keys = Object.keys(patch);
      if (keys.length === 1 && 'item' in patch) {
        if (patch.item) {
          itemDepth.current += 1;
          void navigate({ search: (s) => ({ ...s, item: patch.item }), resetScroll: false });
          return;
        }
        if (itemDepth.current > 0) {
          const depth = itemDepth.current;
          itemDepth.current = 0;
          router.history.go(-depth);
          return;
        }
        void navigate({ search: ({ item: _item, ...s }) => s, replace: true, resetScroll: false });
        return;
      }
      void navigate({ search: (s) => ({ ...s, ...patch }), replace: 'tab' in patch, resetScroll: false });
    },
    [navigate, router],
  );
  return <CasePage caseId={caseId} activity={activity} tab={tab} item={item} onSearch={onSearch} />;
}
